import { useEffect, useRef } from "react";

/*
  Seamless top-to-bottom scroll of the team code list.

  The list is rendered twice; when the scroll position reaches the
  start of the duplicate group it wraps back by exactly that distance,
  so the loop has no visible seam. Native scrolling still works —
  manual interaction just pauses the automatic movement briefly.
*/

const SCROLL_SPEED = 18; // px per second
const START_DELAY = 1200;
const RESUME_DELAY = 3000;

export default function useTeamCodesAutoScroll(viewportRef, duplicateRef, enabled) {
  const frameRef = useRef(null);
  const lastTimeRef = useRef(null);
  const positionRef = useRef(0);
  const pausedRef = useRef(false);
  const startTimerRef = useRef(null);
  const resumeTimerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const viewport = viewportRef.current;
    const duplicate = duplicateRef.current;
    if (!viewport || !duplicate) return undefined;

    viewport.scrollTop = 0;
    positionRef.current = 0;
    pausedRef.current = false;
    lastTimeRef.current = null;

    const pauseForInteraction = () => {
      pausedRef.current = true;
      lastTimeRef.current = null;
      positionRef.current = viewport.scrollTop;

      if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);

      resumeTimerRef.current = window.setTimeout(() => {
        positionRef.current = viewport.scrollTop;
        pausedRef.current = false;
        lastTimeRef.current = null;
        resumeTimerRef.current = null;
      }, RESUME_DELAY);
    };

    const syncManualScroll = () => {
      if (pausedRef.current) positionRef.current = viewport.scrollTop;
    };

    const step = (timestamp) => {
      // The duplicate group starts immediately after the original.
      const resetPoint = duplicate.offsetTop;

      if (resetPoint <= 0) {
        frameRef.current = window.requestAnimationFrame(step);
        return;
      }

      if (lastTimeRef.current === null) lastTimeRef.current = timestamp;

      // Cap the delta so a backgrounded tab doesn't jump the list.
      const elapsed = Math.min(timestamp - lastTimeRef.current, 50);
      lastTimeRef.current = timestamp;

      if (pausedRef.current) {
        positionRef.current = viewport.scrollTop;
        frameRef.current = window.requestAnimationFrame(step);
        return;
      }

      // Normalize a manually chosen position inside the duplicate.
      if (positionRef.current >= resetPoint) {
        positionRef.current = positionRef.current % resetPoint;
      }

      positionRef.current += (SCROLL_SPEED * elapsed) / 1000;

      if (positionRef.current >= resetPoint) {
        positionRef.current -= resetPoint;
      }

      viewport.scrollTop = positionRef.current;
      frameRef.current = window.requestAnimationFrame(step);
    };

    viewport.addEventListener("wheel", pauseForInteraction, { passive: true });
    viewport.addEventListener("touchstart", pauseForInteraction, { passive: true });
    viewport.addEventListener("pointerdown", pauseForInteraction, { passive: true });
    viewport.addEventListener("scroll", syncManualScroll, { passive: true });

    startTimerRef.current = window.setTimeout(() => {
      lastTimeRef.current = null;
      frameRef.current = window.requestAnimationFrame(step);
      startTimerRef.current = null;
    }, START_DELAY);

    return () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      if (startTimerRef.current) window.clearTimeout(startTimerRef.current);
      if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);

      frameRef.current = null;
      startTimerRef.current = null;
      resumeTimerRef.current = null;
      lastTimeRef.current = null;
      positionRef.current = 0;
      pausedRef.current = false;

      viewport.removeEventListener("wheel", pauseForInteraction);
      viewport.removeEventListener("touchstart", pauseForInteraction);
      viewport.removeEventListener("pointerdown", pauseForInteraction);
      viewport.removeEventListener("scroll", syncManualScroll);
    };
  }, [viewportRef, duplicateRef, enabled]);
}
